import React, { useState, useEffect, useRef } from "react"
import CMSRender from "aldoo-ra/CMS/cms-render"
import Typography from "aldoo-ra/Typography"

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 1024)
    checkMobile()
    window.addEventListener("resize", checkMobile)
    return () => window.removeEventListener("resize", checkMobile)
  }, [])

  return isMobile
}

// Define keyframes animation style
const fadeInKeyframes = `
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  .fade-in {
    animation: fadeIn 1200ms cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
  }
`

const Card = ({
  icon,
  title,
  description,
  isExpanded,
  isShrunk,
  isUpperCard,
  height,
}) => {
  const transitionStyle = {
    transitionProperty: "height, transform, margin, padding, opacity",
    transitionTimingFunction:
      isExpanded || isShrunk
        ? "cubic-bezier(0.34, 1.56, 0.64, 1)"
        : "ease-in-out",
    transitionDuration: "600ms",
    transformOrigin: isUpperCard ? "top" : "bottom",
  }

  return (
    <>
      <style>{fadeInKeyframes}</style>
      <div
        className={`
          bg-theme_white text-text rounded-[24px] shadow-custom_card overflow-hidden
          cursor-pointer
          relative
          opacity-0 fade-in
        `}
        style={{
          ...transitionStyle,
          height: `${height}px`,
        }}
        aria-expanded={isExpanded}
      >
        <div className={`h-full flex flex-col ${isShrunk ? 'justify-center' : 'justify-end'} text-left px-10 pt-7 pb-9`}>
          {!isShrunk && (
            <div
              className={`w-[107px] !h-[110px] flex flex-shrink-0 items-end justify-center opacity-0 fade-in ${
                isExpanded ? "my-10 xl:my-14" : "m-0"
              }`}
              style={transitionStyle}
            >
              <img
                src={icon}
                alt={title}
                className="h-full w-auto object-contain mt-auto opacity-0 fade-in"
                style={transitionStyle}
              />
            </div>
          )}

          <div
            className={`w-full flex flex-col ${isShrunk ? 'items-center justify-center' : 'items-center justify-end'} h-full opacity-0 fade-in`}
            style={transitionStyle}
          >
            <Typography
              className="text-left text-[20px] md:text-[24px] lg:text-[22px] leading-normal font-bold tracking-custom_wide_1 mr-auto my-0 opacity-0 fade-in"
              style={transitionStyle}
            >
              {title}
            </Typography>

            {isExpanded && (
              <Typography
                className="text-text/80 text-left text-[16px] leading-normal tracking-custom_1 font-normal mt-4 mr-auto opacity-0"
                style={{
                  ...transitionStyle,
                  animation:
                    "fadeIn 1200ms cubic-bezier(0.34, 1.56, 0.64, 1) forwards 200ms",
                }}
              >
                {description}
              </Typography>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

const CardColumn = ({ upperCard, lowerCard, totalHeight = 500 }) => {
  const [expandedIndex, setExpandedIndex] = useState(null)
  const upperCardRef = useRef(null)
  const lowerCardRef = useRef(null)
  const isMobile = useIsMobile()

  const collapsedHeight = totalHeight / 2
  const expandedHeight = totalHeight * 0.8
  const shrunkHeight = totalHeight * 0.2

  useEffect(() => {
    if (!isMobile) return

    let lastExpandedIndex = null
    let lastCheckTime = 0
    const DEBOUNCE_TIME = 50
    const HYSTERESIS = 0.2

    const checkCardPosition = () => {
      if (!upperCardRef.current || !lowerCardRef.current) return

      const now = Date.now()
      if (now - lastCheckTime < DEBOUNCE_TIME) return
      lastCheckTime = now

      const viewportHeight = window.innerHeight
      const viewportCenter = viewportHeight / 2
      const threshold = totalHeight / 2

      const cards = [
        { ref: upperCardRef, index: 0 },
        { ref: lowerCardRef, index: 1 },
      ].map(({ ref, index }) => {
        const rect = ref.current.getBoundingClientRect()
        const center = rect.top + rect.height / 2
        return {
          index,
          center,
          distance: Math.abs(center - viewportCenter),
        }
      })

      cards.sort((a, b) => a.distance - b.distance)

      const closestCard = cards[0]
      const currentlyExpanded = lastExpandedIndex

      if (currentlyExpanded === null) {
        if (closestCard.distance < threshold) {
          lastExpandedIndex = closestCard.index
          setExpandedIndex(closestCard.index)
        }
      } else {
        const expandedCard = cards.find((c) => c.index === currentlyExpanded)
        const hysteresisThreshold = threshold * (1 + HYSTERESIS)

        if (
          expandedCard.distance > hysteresisThreshold &&
          closestCard.distance > threshold
        ) {
          lastExpandedIndex = null
          setExpandedIndex(null)
        } else if (
          closestCard.index !== currentlyExpanded &&
          closestCard.distance < threshold * (1 - HYSTERESIS)
        ) {
          lastExpandedIndex = closestCard.index
          setExpandedIndex(closestCard.index)
        }
      }
    }

    let ticking = false
    const scrollHandler = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          checkCardPosition()
          ticking = false
        })
        ticking = true
      }
    }

    window.addEventListener("scroll", scrollHandler)
    checkCardPosition()

    return () => window.removeEventListener("scroll", scrollHandler)
  }, [isMobile, totalHeight])

  const handleMouseInteraction = (index) => {
    if (!isMobile) {
      setExpandedIndex(index)
    }
  }

  return (
    <div className="flex flex-col gap-10 opacity-0 fade-in">
      <div
        ref={upperCardRef}
        onMouseEnter={() => handleMouseInteraction(0)}
        onMouseLeave={() => handleMouseInteraction(null)}
      >
        <Card
          {...upperCard}
          isExpanded={expandedIndex === 0}
          isShrunk={expandedIndex === 1}
          isUpperCard={true}
          height={
            expandedIndex === 0
              ? expandedHeight
              : expandedIndex === 1
              ? shrunkHeight
              : collapsedHeight
          }
        />
      </div>
      <div
        ref={lowerCardRef}
        onMouseEnter={() => handleMouseInteraction(1)}
        onMouseLeave={() => handleMouseInteraction(null)}
      >
        <Card
          {...lowerCard}
          isExpanded={expandedIndex === 1}
          isShrunk={expandedIndex === 0}
          isUpperCard={false}
          height={
            expandedIndex === 1
              ? expandedHeight
              : expandedIndex === 0
              ? shrunkHeight
              : collapsedHeight
          }
        />
      </div>
    </div>
  )
}

const AffiliateSection = () => {
  return (
    <CMSRender
      contentType="AquaSiteCardGroup"
      match={{ "content.id": "affiliate-if-you-are" }}
      renderItem={(group, id) => {
        const columns = Array(3)
          .fill(null)
          .map((_, i) => [group.cards[i], group.cards[i + 3]])
        return (
          <div className="w-full px-4 md:px-14 xl:px-20 opacity-0 fade-in">
            <div key={id} className="w-full max-w-w1_xl mx-auto">
              <Typography
                component="h2"
                variant="h1"
                className="text-center mb-[60px] opacity-0 fade-in"
              >
                {group.title}
              </Typography>

              <div
                className="
                  grid grid-cols-1
                  w-full mx-auto
                  max-w-[500px]
                  gap-10
                  opacity-0 fade-in

                  lg:grid-cols-3
                  lg:max-w-[900px]
                  xl:max-w-w2_xl
                  2xl:max-w-w1_xl
                  lg:[&>*]:min-w-0
                  lg:[&>*]:w-full

                  xl:[&>*]:max-w-[432px]

                  lg:[&>*:nth-child(1)]:justify-self-start
                  lg:[&>*:nth-child(2)]:justify-self-center
                  lg:[&>*:nth-child(3)]:justify-self-end
                "
                style={{
                  gap: "40px",
                  "@media (minWidth: 1080px)": {
                    gap: "clamp(16px, calc(3vw - 16px), 40px)",
                  },
                  "@media (minWidth: 1280px)": {
                    gap: "clamp(16px, calc(5vw - 16px), 62px)",
                  },
                }}
              >
                {columns.map((column, index) => (
                  <CardColumn
                    key={index}
                    upperCard={column[0]}
                    lowerCard={column[1]}
                  />
                ))}
              </div>
            </div>
          </div>
        )
      }}
    />
  )
}

export default AffiliateSection

export const AldooComponent = {
  id: 'AffiliateSection2',
  component: AffiliateSection,
  meta: {
    name: 'Affiliate Section 2',
    description: 'Interactive card section with expandable cards in three columns',
    category: 'Marketing',
  },
}
